/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { isNumber, uniq } from 'lodash'

import type { InstanceConfiguration, MetadataItem } from '@modules/cloud-api/v1/types'
import type {
  AnyTopologyElement,
  NodeConfiguration,
  NodeRole,
  SliderInstanceType,
} from '@modules/ui-types'
import type { InnerClause } from '@modules/ui-types/instanceConfigurationTypes'

import { allocatorFilterToClauses } from '../allocatorFilters/allocatorFilterClauses'
import {
  getSliderNodeTypeForTopologyElement,
  isRoleAnyDataRole,
} from '../stackDeployments/selectors/nodeRoles'

export function getInstanceConfigurationById(
  instanceConfigurations: InstanceConfiguration[],
  id: string,
): InstanceConfiguration | undefined {
  return instanceConfigurations.find((instanceConfiguration) => instanceConfiguration.id === id)
}

export function getInstanceConfigurationByIdAndVersion(
  instanceConfigurations: InstanceConfiguration[],
  id: string,
  version?: number,
): InstanceConfiguration | undefined {
  if (version !== undefined) {
    // If the topologyElement has an IC version defined, then we need to search for that and the ID to find the right IC.
    return instanceConfigurations.find(
      (instanceConfiguration) =>
        instanceConfiguration.id === id && instanceConfiguration.config_version === version,
    )
  }

  // If the topologyElement doesn't have an IC version defined, then we need to search for the ID only to find the right IC.
  // I'm splitting this out because config_version in the IC could be defined, but we may not have a version in the topologyElement.
  // That would mean that the "ic.config_version === topologyElement.instance_configuration_version" check would fail and we wouldn't
  // find an IC even though version doesn't actually matter (since it's not defined in the topologyElement)

  const matchingICs = instanceConfigurations.filter(
    (instanceConfiguration) => instanceConfiguration.id === id,
  )

  if (matchingICs.length === 0) {
    return undefined
  }

  if (matchingICs.length === 1) {
    return matchingICs[0]
  }

  // find the IC with the latest config_version
  return matchingICs.reduce((acc, ic) => {
    if (acc.config_version === undefined) {
      return ic
    }

    if (ic.config_version === undefined) {
      return acc
    }

    if (ic.config_version > acc.config_version) {
      return ic
    }

    return acc
  })
}

function isDedicatedConfig(config: InstanceConfiguration): boolean {
  return (config.node_types || []).length === 1
}

export function isDeprecatedConfig(config: InstanceConfiguration): boolean {
  return config.metadata ? 'deprecated' in config.metadata : false
}

function getInstanceConfigsByNodeType(
  instanceConfigurations: InstanceConfiguration[] = [],
  type: string,
  allowMulti: boolean = true,
  ...forceInclude: Array<{ id: string }>
): InstanceConfiguration[] {
  return instanceConfigurations.filter((config) => {
    if (!config.node_types || !config.node_types.includes(type)) {
      return false
    }

    if (forceInclude && forceInclude.some((c) => c.id === config.id)) {
      return true
    }

    return !isDeprecatedConfig(config) && (allowMulti || isDedicatedConfig(config))
  })
}

function getInstanceConfigsByType(instanceConfigurations: InstanceConfiguration[], type: string) {
  return instanceConfigurations.filter((config) => config.instance_type === type)
}

export function filterToRelevantInstanceConfigs({
  sliderInstanceType,
  topologyElement,
  instanceConfigurations,
}: {
  sliderInstanceType: SliderInstanceType
  topologyElement: AnyTopologyElement
  instanceConfigurations: InstanceConfiguration[]
}): InstanceConfiguration[] {
  const sliderNodeType = getSliderNodeTypeForTopologyElement({ topologyElement }) as NodeRole

  if (sliderNodeType) {
    const instanceConfigNodeType = isRoleAnyDataRole(sliderNodeType) ? `data` : sliderNodeType

    const forceInclude: Array<{ id: string }> = []

    if (topologyElement.instance_configuration_id) {
      forceInclude.push({
        id: topologyElement.instance_configuration_id,
      })
    }

    return getInstanceConfigsByNodeType(
      instanceConfigurations,
      instanceConfigNodeType,
      true,
      ...forceInclude,
    )
  }

  // If there's no specific node_type to filter against, fall back to
  // returning all instance-compatible configurations. Partly to handle
  // legitimate node_type-supporting but non-ES products, e.g. Enterprise
  // Search; partly just sanity.
  return getInstanceConfigsByType(instanceConfigurations, sliderInstanceType)
}

export function getInstanceConfigsFromNodeConfigs(
  nodeConfigurations: NodeConfiguration[],
): string[] {
  const configurationIds = nodeConfigurations.map(
    (nodeConfiguration) => nodeConfiguration.instance_configuration_id,
  )
  const validConfigurationIds = configurationIds.filter(Boolean) as string[]
  const uniques = uniq(validConfigurationIds)

  return uniques
}

//  Gets the instances that have a filter that matches a specified allocator tag
export function getInstancesWithMatchingFilter({
  instances,
  tag,
}: {
  instances: any
  tag: MetadataItem
}) {
  return instances.filter((instance) => {
    const clauses = allocatorFilterToClauses(instance.instanceConfiguration.allocator_filter)
    const innerClauses: InnerClause[] = []
    clauses.forEach((clause) => {
      clause.innerClauses.forEach((innerClause) => {
        innerClauses.push(innerClause)
      })
    })
    const matchingClauses = innerClauses.filter(
      (innerClause) => innerClause.value === tag.value && innerClause.key === tag.key,
    )

    return matchingClauses.length > 0
  })
}

export function hasMaxZones(instanceConfigurations: InstanceConfiguration[]): boolean {
  return instanceConfigurations.some(({ max_zones }) => isNumber(max_zones))
}
